/**
 * PromotionPill — Clean promotion row for PricingCard.
 *
 * Line 1: Status dot + title + discount badge + overflow menu
 * Line 2: Colored scope badge + date
 *
 * Storefront toggles (eye/store) live inside the overflow menu —
 * they're set-once controls, not daily interactions.
 *
 * Part of Plan 34 (Pricing Engine), Epic 04.
 */

import { cn } from "@/lib/utils";
import { MoreHorizontal, Pencil, XCircle, Eye, Store, Tag, Layers, FolderOpen, Building2, ShoppingBag } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { usePageTranslation } from "@/i18n/useTranslation";
import { inventoryTranslations as enInventory } from "@/i18n/locales/en/inventory";
import { inventoryTranslations as thInventory } from "@/i18n/locales/th/inventory";
import type { Promotion } from "@/features/inventory/api/pricingSummaryHooks";
import { inferPromotionTypeFromRule } from "./promotionTypes";

// ── Status badge colors ─────────────────────────────────────

export const STATUS_STYLES: Record<string, { dot: string; bg: string; text: string }> = {
  Active: {
    dot: "bg-emerald-500",
    bg: "bg-emerald-500/10 border-emerald-500/20",
    text: "text-emerald-700 dark:text-emerald-400",
  },
  Scheduled: {
    dot: "bg-amber-500",
    bg: "bg-amber-500/10 border-amber-500/20",
    text: "text-amber-700 dark:text-amber-400",
  },
  Expired: {
    dot: "bg-gray-400",
    bg: "bg-gray-100 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700",
    text: "text-gray-500 dark:text-gray-400",
  },
};

// ── Scope badge styles ──────────────────────────────────────

const SCOPE_STYLES: Record<string, { icon: typeof Tag; bg: string; text: string }> = {
  item: {
    icon: Tag,
    bg: "bg-sky-500/10",
    text: "text-sky-700 dark:text-sky-400",
  },
  variants: {
    icon: Layers,
    bg: "bg-violet-500/10",
    text: "text-violet-700 dark:text-violet-400",
  },
  category: {
    icon: FolderOpen,
    bg: "bg-amber-500/10",
    text: "text-amber-700 dark:text-amber-400",
  },
  brand: {
    icon: Building2,
    bg: "bg-orange-500/10",
    text: "text-orange-700 dark:text-orange-400",
  },
  store: {
    icon: ShoppingBag,
    bg: "bg-rose-500/10",
    text: "text-rose-700 dark:text-rose-400",
  },
};

// ── Helpers ──────────────────────────────────────────────────

export function formatDiscount(promo: Promotion): string {
  const rod = promo.rate_or_discount || "";
  if (rod === "Discount Percentage" && promo.discount_percentage != null) {
    return `\u2212${Number(promo.discount_percentage).toFixed(0)}%`;
  }
  if (rod === "Discount Amount" && promo.discount_amount != null) {
    return `\u2212${Number(promo.discount_amount).toFixed(0)}`;
  }
  if (rod === "Rate" && promo.rate != null) {
    return `${Number(promo.rate).toFixed(2)}`;
  }
  return "";
}

export function formatShortDate(dateStr: string | null): string {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${months[d.getMonth()]} ${d.getDate()}`;
  } catch {
    return dateStr;
  }
}

/** Extract entity name from scope label like "Category: Furniture" → "Furniture" */
function extractEntityName(scope: string): string {
  const idx = scope.indexOf(": ");
  return idx >= 0 ? scope.slice(idx + 2) : scope;
}

// ── Props ────────────────────────────────────────────────────

interface PromotionPillProps {
  promo: Promotion;
  isEnding?: boolean;
  isCollapsing?: boolean;
  onEdit?: (promo: Promotion) => void;
  onEnd?: (promo: Promotion) => void;
  onConfirmEnd?: (promo: Promotion) => void;
  onCancelEnd?: () => void;
  onToggleShowOnDetail?: (promo: Promotion) => void;
  onToggleStorefrontEnabled?: (promo: Promotion) => void;
  onToggleDisabled?: (promo: Promotion) => void;
}

// ── Component ────────────────────────────────────────────────

export function PromotionPill({
  promo,
  isEnding,
  isCollapsing,
  onEdit,
  onEnd,
  onConfirmEnd,
  onCancelEnd,
  onToggleShowOnDetail,
  onToggleStorefrontEnabled,
  onToggleDisabled,
}: PromotionPillProps) {
  const t = usePageTranslation({ en: enInventory, th: thInventory });
  const styles = STATUS_STYLES[promo.status] || STATUS_STYLES.Expired;
  const discountLabel = formatDiscount(promo);
  const canEdit = inferPromotionTypeFromRule(promo) !== null;
  const isExpired = promo.status === "Expired";

  // Scope badge
  const scopeType = promo.scope_type || "store";
  const scopeStyle = SCOPE_STYLES[scopeType] || SCOPE_STYLES.store;
  const ScopeIcon = scopeStyle.icon;

  // Collapsing animation
  if (isCollapsing) {
    return (
      <div
        className="overflow-hidden transition-all duration-200 ease-in"
        style={{ maxHeight: 0, opacity: 0, marginTop: 0, marginBottom: 0 }}
      />
    );
  }

  // Inline "End now" confirmation
  if (isEnding) {
    return (
      <div className={cn(
        "flex flex-col gap-2 px-3 py-3 rounded-md border text-sm",
        "border-red-300 bg-red-50 dark:bg-red-950/20 dark:border-red-800"
      )}>
        <p className="text-xs text-red-700 dark:text-red-400">
          {t.promoEndConfirmation}
        </p>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="destructive"
            onClick={() => onConfirmEnd?.(promo)}
            className="rounded-full text-xs h-7 px-3"
          >
            {t.promoEndSale}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onCancelEnd}
            className="rounded-full text-xs h-7 px-3"
          >
            {t.promoKeepIt}
          </Button>
        </div>
      </div>
    );
  }

  // Date label
  let dateLabel = "";
  if (promo.valid_upto) {
    dateLabel = `${t.promoEnds} ${formatShortDate(promo.valid_upto)}`;
  } else if (promo.valid_from) {
    dateLabel = `${t.promoFrom} ${formatShortDate(promo.valid_from)}`;
  }

  const isDisabled = !!promo.disabled;

  return (
    <div className={cn(
      "rounded-md border text-sm transition-opacity",
      styles.bg,
      isDisabled && "opacity-50",
    )}>
      {/* Line 1: dot + title + discount + menu */}
      <div className="flex items-center gap-2 px-3 pt-2 pb-1">
        <div className={cn("w-2 h-2 rounded-full shrink-0", styles.dot)} />
        <span className="text-xs font-medium text-foreground truncate">
          {promo.title}
        </span>
        {discountLabel && (
          <span className="text-xs font-semibold text-foreground/80 shrink-0">
            {discountLabel}
          </span>
        )}

        {/* Eye = which badge to show on PDP (radio). Store = which rules apply at checkout (independent). */}
        <div className="ml-auto flex items-center gap-0.5 shrink-0">
          {/* Eye toggle — badge display (radio: one per item) */}
          <button
            type="button"
            onClick={() => onToggleShowOnDetail?.(promo)}
            disabled={isExpired}
            className={cn(
              "p-1.5 rounded-md transition-colors disabled:opacity-40",
              promo.storefront_show_on_detail
                ? "text-amber-500 hover:bg-amber-500/10"
                : "text-muted-foreground/40 hover:bg-black/5 dark:hover:bg-white/10"
            )}
            aria-label={promo.storefront_show_on_detail ? t.promoHideBadge : t.promoShowBadge}
            title={promo.storefront_show_on_detail ? t.promoHideBadge : t.promoShowBadge}
          >
            <Eye className="h-4 w-4" />
          </button>

          {/* Store toggle — checkout pricing (independent checkbox) */}
          <button
            type="button"
            onClick={() => onToggleStorefrontEnabled?.(promo)}
            disabled={isExpired}
            className={cn(
              "p-1.5 rounded-md transition-colors disabled:opacity-40",
              promo.storefront_enabled
                ? "text-sky-500 hover:bg-sky-500/10"
                : "text-muted-foreground/40 hover:bg-black/5 dark:hover:bg-white/10"
            )}
            aria-label={promo.storefront_enabled ? t.promoDisableStorefront : t.promoEnableStorefront}
            title={promo.storefront_enabled ? t.promoDisableStorefront : t.promoEnableStorefront}
          >
            <Store className="h-4 w-4" />
          </button>

          {/* Overflow menu — Edit + End only */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="p-1.5 rounded-md hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
                aria-label={t.promoActions}
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem
                onClick={() => onEdit?.(promo)}
                disabled={!canEdit}
              >
                <Pencil className="h-3.5 w-3.5 mr-2" />
                {t.promoEditPromotion}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onEnd?.(promo)}>
                <XCircle className="h-3.5 w-3.5 mr-2" />
                {t.promoEndNow}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Line 2: scope badge + date + enable/disable switch */}
      <div className="flex items-center gap-2 px-3 pb-2 pl-7">
        {promo.scope && (
          <span className={cn(
            "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium",
            scopeStyle.bg, scopeStyle.text,
          )}>
            <ScopeIcon className="h-2.5 w-2.5" />
            {scopeType === "item" ? t.promoScopeItem
              : scopeType === "variants" ? t.promoScopeVariants
              : scopeType === "category" ? `${t.promoScopeCategory} ${extractEntityName(promo.scope)}`
              : scopeType === "brand" ? `${t.promoScopeBrand} ${extractEntityName(promo.scope)}`
              : t.promoScopeStore}
          </span>
        )}
        {dateLabel && (
          <span className="text-xs text-muted-foreground">
            {dateLabel}
          </span>
        )}

        {/* Enable/Disable switch */}
        {!isExpired && (
          <div className="ml-auto flex items-center gap-1.5">
            <span className="text-[11px] text-muted-foreground">
              {isDisabled ? t.promoDisabled : t.promoEnabled}
            </span>
            <Switch
              checked={!isDisabled}
              onCheckedChange={() => onToggleDisabled?.(promo)}
              className="scale-75 origin-right"
              aria-label={isDisabled ? t.promoEnabled : t.promoDisabled}
            />
          </div>
        )}
      </div>
    </div>
  );
}
